import { useTranslation } from 'react-i18next';

import { Box, Chip, Typography } from '@mui/material';

import moment from 'moment';

import { currency } from 'lib/helpers';
import { NfcCardState, isNfcCardExpired } from 'lib/models/nfcCards';

import useNfcCardLabels from 'hooks/useNfcCardLabels';

type Props = {
	card: NfcCard;
	/** The title in a bigger type, for the header of a card screen. */
	large?: boolean;
};

const DATE_FORMAT = 'DD/MM/YYYY';

const stateColors: Record<NfcCardState, 'success' | 'error' | 'warning'> = {
	active: 'success',
	blocked: 'error',
	expired: 'warning',
};

/**
 * What identifies a card at a glance: label, state, responsable and expiration, and
 * its balance and number of operations when the card carries them (the listing answers
 * them, a card read on its own does not). Shared by the cards of the listing and the header
 * of each card screen. The UID is never shown: the panel does not have it (MUTE-606), so a
 * card goes by its label.
 *
 * A card past its expiration date is flagged with an "expired" chip next to its state, which
 * the API leaves as it was: the operator sees it as soon as the card is read, rather than
 * when an action is refused (MUTE-622).
 */
export default function NfcCardSummary({ card, large }: Props) {
	const { t } = useTranslation();
	const { stateLabel } = useNfcCardLabels();

	const expired = isNfcCardExpired(card);

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
			<Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
				<Typography
					variant={large ? 'h6' : 'subtitle1'}
					color={card.label ? undefined : 'text.secondary'}
					sx={{ overflowWrap: 'anywhere', lineHeight: 1.3 }}
				>
					{card.label || t('nfc:cardWithoutLabel')}
				</Typography>
				<Chip size="small" label={stateLabel(card.state)} color={stateColors[card.state]} />
				{expired && card.state !== 'expired' && (
					<Chip size="small" label={stateLabel('expired')} color={stateColors.expired} />
				)}
			</Box>

			{!!card.responsable && (
				<Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
					{t('nfc:responsable')}: {card.responsable}
				</Typography>
			)}

			{(typeof card.balance === 'number' || typeof card.operations === 'number') && (
				<Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', columnGap: 2, alignItems: 'baseline' }}>
					{typeof card.balance === 'number' && (
						<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
							{t('nfc:balance')}: {currency(card.balance)}
						</Typography>
					)}
					{typeof card.operations === 'number' && (
						<Typography variant="body2" color="text.secondary">
							{t('nfc:operationsCount', { count: card.operations })}
						</Typography>
					)}
				</Box>
			)}

			<Typography variant="body2" color={expired ? 'error' : 'text.secondary'}>
				{t('nfc:expiresAt')}: {card.expiresAt ? moment(card.expiresAt).format(DATE_FORMAT) : '-'}
				{expired ? ` ${t('nfc:expiredTag')}` : ''}
			</Typography>
		</Box>
	);
}
